/**
 * The RAG sources an AI result cites (specs/systemic-v2-grounded-generation.md
 * G5; room-review §1), as the shared SourceChip (opens the chunk). "The source
 * service did not answer" is said as such, never shown as an empty list — a
 * rationale that could not be checked must not look like one that was.
 *
 * Research comes first. The workshop's own material (tier 'human') follows
 * under its own heading: it is the room's text, never evidence for the room's
 * claims. Web pages (tier 'web') come last, under their own heading, as
 * WebSourceChip: they were searched online, not taken from the vetted corpus,
 * and must not look like it (specs/systemic-v2-analysis-depth.md). Sources are
 * numbered by position. The S/V/W markers they carry belonged to the prompt that
 * cited them and mean nothing here.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Globe } from 'lucide-react';
import SourceChip from '../../components/SourceChip';
import SourceTag from '../../components/AIReferences/SourceTag';
import WebSourceChip from './WebSourceChip';
import Disclosure from '../../components/ui/Disclosure';
import { useTranslation } from '../../hooks/useTranslation';

const Row = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px;`;
// Kinds of source shown in a row of their own, after research (specs/knowledge-kinds.md)
const KIND_ROWS = ['policy', 'opinion', 'testimony'];
const Note = styled.span`font-size: 12px; color: rgba(255,255,255,0.6);`;
const WebHead = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 10px;
  font-size: 0.7rem; font-weight: 600; color: #5eead4;
  background: rgba(45, 212, 191, 0.1); border: 1px solid rgba(45, 212, 191, 0.4);
`;

/** What SourceChip reads: the document title (its own cleanCitation would cut a "title | section" citation), section and page. */
const chipSource = (s, n) => ({
  ...s,
  marker: String(n),
  citation: s.title || s.citation,
  section_heading: s.section ?? s.section_heading ?? null,
  page_number: s.page ?? s.page_number ?? null,
});

/**
 * Folded by default behind "Lähteet (n)" (#1194): an always-open row of chips
 * under every AI item was the most repeated clutter testers met. That the
 * source service did not answer is never folded away.
 */
export default function SourceList({ sources = [], unavailable = false, showNone = false, folded = true }) {
  const { t } = useTranslation();
  if (unavailable) return <Row><Note>{t('systemicMapV2.sources.unavailable')}</Note></Row>;
  if (!sources.length) return showNone ? <Row><Note>{t('systemicMapV2.sources.none')}</Note></Row> : null;
  // Policy, opinion and testimony sources get their own labelled row, never the research one (specs/knowledge-kinds.md)
  const ofKind = (kind) => sources.filter((s) => s.tier !== 'human' && s.tier !== 'web' && s.knowledgeKind === kind);
  const research = sources.filter((s) => s.tier !== 'human' && s.tier !== 'web' && !KIND_ROWS.includes(s.knowledgeKind));
  const kinds = KIND_ROWS.map((kind) => [kind, ofKind(kind)]).filter(([, rows]) => rows.length);
  const workshop = sources.filter((s) => s.tier === 'human');
  const web = sources.filter((s) => s.tier === 'web');
  const kindCount = kinds.reduce((n, [, rows]) => n + rows.length, 0);
  const workshopStart = research.length + kindCount;
  const webStart = workshopStart + workshop.length;
  let numbered = research.length;
  const list = (
    <>
      {research.length ? (
        <Row>
          {research.map((s, i) => <SourceChip key={s.chunkId || s.marker || i} source={chipSource(s, i + 1)} />)}
        </Row>
      ) : null}
      {kinds.map(([kind, rows]) => {
        const start = numbered;
        numbered += rows.length;
        return (
          <Row key={kind}>
            <SourceTag tier={kind} />
            {rows.map((s, i) => <SourceChip key={s.chunkId || s.marker || i} source={chipSource(s, start + i + 1)} />)}
          </Row>
        );
      })}
      {workshop.length ? (
        <Row>
          <SourceTag tier="human" label={t('systemicMapV2.sources.workshop')} />
          {workshop.map((s, i) => (
            <SourceChip key={s.chunkId || s.marker || i} source={chipSource(s, workshopStart + i + 1)} />
          ))}
        </Row>
      ) : null}
      {web.length ? (
        <>
          <Row>
            <WebHead><Globe size={12} aria-hidden="true" />{t('systemicMapV2.sources.web')}</WebHead>
            <Note>{t('systemicMapV2.sources.webNote')}</Note>
          </Row>
          <Row>
            {web.map((s, i) => <WebSourceChip key={s.url || i} source={s} number={webStart + i + 1} />)}
          </Row>
        </>
      ) : null}
    </>
  );
  if (!folded) return list;
  return (
    <Disclosure variant="inline" summary={t('systemicMapV2.sources.fold', { count: sources.length })} testId="sources-fold">
      {list}
    </Disclosure>
  );
}
